import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { submitSupportForm } from '../../services/support-service';
import {
  countWords,
  validateSupportForm,
  SupportFormData,
  SupportSubmitPayload,
} from '../../utils/supportUtils';

interface SupportFormDialogProps {
  open: boolean;
  onClose: () => void;
}

const MAX_WORDS = 600;

const SupportFormDialog: React.FC<SupportFormDialogProps> = ({ open, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();

  const [formData, setFormData] = useState<SupportFormData>({
    type: 'Feedback',
    text: '',
    outfitId: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [textTouched, setTextTouched] = useState(false);

  const wordCount = countWords(formData.text);
  const validation = validateSupportForm({ type: formData.type, text: formData.text });

  const resetForm = useCallback(() => {
    setFormData({ type: 'Feedback', text: '', outfitId: '' });
    setTextTouched(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  }, [submitting, resetForm, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!validation.valid || submitting) return;

    setSubmitting(true);
    try {
      const payload: SupportSubmitPayload = {
        type: formData.type,
        text: formData.text,
      };

      // Include outfit ID if provided
      if (formData.outfitId && formData.outfitId.trim()) {
        payload.outfitId = formData.outfitId.trim();
      }

      // Include user context if authenticated
      if (isAuthenticated && user) {
        payload.userId = user.id;
        payload.userEmail = user.email;
        payload.userDisplayName = user.displayName;
        payload.username = user.username;
      }

      const result = await submitSupportForm(payload);

      if (result.success) {
        showToast('Your feedback has been submitted. Thank you!', 'success');
        resetForm();
        onClose();
      } else {
        showToast('Something went wrong. Please try again.', 'error');
      }
    } catch {
      showToast('Something went wrong. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [validation.valid, submitting, formData, isAuthenticated, user, showToast, resetForm, onClose]);

  const isOverLimit = wordCount > MAX_WORDS;
  const isTextEmpty = wordCount === 0;
  const submitDisabled = submitting || !validation.valid;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="support-dialog-title"
    >
      <DialogTitle id="support-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Contact Support
        <IconButton aria-label="close" onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="support-type-label">Type</InputLabel>
          <Select
            labelId="support-type-label"
            id="support-type-select"
            value={formData.type}
            label="Type"
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, type: e.target.value as 'Issue' | 'Feedback' }))
            }
            disabled={submitting}
          >
            <MenuItem value="Issue">Issue</MenuItem>
            <MenuItem value="Feedback">Feedback</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Description"
          multiline
          minRows={4}
          maxRows={10}
          fullWidth
          value={formData.text}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, text: e.target.value }));
            if (!textTouched) setTextTouched(true);
          }}
          disabled={submitting}
          error={textTouched && (isTextEmpty || isOverLimit)}
          helperText={
            textTouched && isTextEmpty
              ? 'Please enter a description'
              : textTouched && isOverLimit
              ? `Your message exceeds the ${MAX_WORDS} word limit`
              : ''
          }
          placeholder="Describe your issue or feedback..."
          sx={{ mb: 1 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Typography
            variant="caption"
            color={isOverLimit ? 'error' : 'text.secondary'}
          >
            {wordCount} / {MAX_WORDS} words
          </Typography>
        </Box>

        <TextField
          label="Outfit ID (optional)"
          fullWidth
          value={formData.outfitId}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, outfitId: e.target.value }))
          }
          disabled={submitting}
          placeholder="Enter outfit ID if applicable"
          helperText="Only needed if your issue relates to a specific outfit"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportFormDialog;
